/** Canonical route manifest used by nav, breadcrumbs, and page metadata. */
export const routeLabels = {
  dashboard: 'Dashboard',
  login: 'Login',
  players: 'Players',
  wallet: 'Wallet',
  deposits: 'Deposits',
  withdrawals: 'Withdrawals',
  transactions: 'Transactions',
  adjustments: 'Adjustments',
  'payment-methods': 'Payment methods',
  tournaments: 'Tournaments',
  tables: 'Tables',
  'sit-and-go': 'Sit & Go',
  fraud: 'Fraud',
  alerts: 'Alerts',
  cases: 'Cases',
  reviews: 'Reviews',
  'shared-endpoints': 'Shared IP / device',
  collusion: 'Collusion',
  'chip-dumping': 'Chip dumping',
  sanctions: 'Sanctions',
  kyc: 'KYC',
  queue: 'Queue',
  logs: 'Logs',
  restrictions: 'Restrictions',
  promotions: 'Promotions',
  bonuses: 'Bonuses',
  codes: 'Promo codes',
  campaigns: 'Campaigns',
  rakeback: 'Rakeback / cashback',
  vip: 'VIP',
  tiers: 'Tiers',
  rules: 'Rules',
  leaderboards: 'Leaderboards',
  missions: 'Missions',
  achievements: 'Achievements',
  support: 'Support',
  tickets: 'Tickets',
  templates: 'Templates',
  cms: 'CMS',
  banners: 'Banners',
  'promo-blocks': 'Promo blocks',
  faq: 'FAQ',
  notifications: 'Notifications',
  announcements: 'Announcements',
  popups: 'Popups',
  reports: 'Reports',
  settings: 'Settings',
  staff: 'Staff',
  roles: 'Roles',
  platform: 'Platform',
  'feature-flags': 'Feature flags',
  security: 'Security',
  overview: 'Overview',
  balances: 'Balances',
  gameplay: 'Gameplay',
  registrations: 'Registrations',
  blinds: 'Blind structure',
  prizes: 'Prize structure',
  schedule: 'Schedule',
  compliance: 'Compliance',
  'notes-tags': 'Notes & tags',
  'forgot-password': 'Forgot password',
  'reset-password': 'Reset password',
  new: 'New',
}

/** @type {Record<string, { title: string, description?: string }>} */
export const staticPageMeta = {
  '/forgot-password': {
    title: 'Forgot password',
    description: 'Request a password reset link sent to the staff email on file.',
  },
  '/reset-password': {
    title: 'Reset password',
    description: 'Choose a new password using your secure reset token.',
  },
  '/security': {
    title: 'Session & security',
    description: 'Active sessions, devices, and security preferences (placeholder).',
  },
  '/wallet/deposits': {
    title: 'Deposits',
    description: 'Monitor inbound payments, reconciliation status, and exceptions.',
  },
  '/wallet/withdrawals': {
    title: 'Withdrawals',
    description: 'Review queue, risk holds, AML checks, and payout execution.',
  },
  '/wallet/adjustments': {
    title: 'Manual adjustments',
    description: 'Ledger corrections with dual-control and immutable audit trail.',
  },
  '/wallet/payment-methods': {
    title: 'Payment methods',
    description: 'Configure PSP routing, limits, and regional availability.',
  },
  '/tournaments/new': {
    title: 'Create tournament',
    description: 'Structured editor for schedule, structure, and satellite relationships.',
  },
  '/sit-and-go': { title: 'Sit & Go', description: 'Templates, rake, and auto-spawn rules for SNG lobbies.' },
  '/sit-and-go/new': { title: 'Create Sit & Go', description: 'Define buy-in ladder, payout curve, and seat counts.' },
  '/fraud/alerts': { title: 'Fraud alerts', description: 'Prioritized queue with severity, linked entities, and assignment.' },
  '/fraud/reviews/shared-endpoints': { title: 'Shared IP / device review', description: 'Correlate accounts sharing endpoints and escalate to cases.' },
  '/fraud/reviews/collusion': { title: 'Collusion review', description: 'Graph-style review workspace for suspicious gameplay patterns.' },
  '/fraud/reviews/chip-dumping': { title: 'Chip dumping review', description: 'Loss-transfer analysis between flagged player dyads.' },
  '/fraud/sanctions': { title: 'Sanctions workflow', description: 'Screening hits, false-positive disposition, and enforcement actions.' },
  '/kyc/queue': { title: 'KYC queue', description: 'Prioritized verification backlog with SLA timers.' },
  '/kyc/logs': { title: 'Compliance logs', description: 'Immutable record of decisions, overrides, and policy versions.' },
  '/kyc/restrictions': { title: 'Restrictions', description: 'Jurisdiction blocks, product blocks, and velocity caps.' },
  '/promotions/bonuses': { title: 'Bonuses', description: 'Catalog of incentives with eligibility matrices and burn-down.' },
  '/promotions/bonuses/new': { title: 'Create bonus', description: 'Wizard for accrual rules, wagering, and conflict resolution.' },
  '/promotions/codes': { title: 'Promo codes', description: 'Single-use and multi-use codes with caps and redemption logs.' },
  '/promotions/rakeback': { title: 'Rakeback / cashback', description: 'Periodic settlement configuration and liability previews.' },
  '/vip/tiers': { title: 'VIP tiers', description: 'Tier ladders, downgrade grace, and concierge routing.' },
  '/vip/rules': { title: 'Loyalty rules', description: 'Points accrual, expiry, and cross-product weighting.' },
  '/leaderboards': { title: 'Leaderboards', description: 'Seasonal boards, anti-abuse caps, and prize fulfillment.' },
  '/missions': { title: 'Missions', description: 'Objective tracks, progress sources, and reward issuance.' },
  '/achievements': { title: 'Achievements', description: 'Badge catalog and unlock telemetry.' },
  '/support/tickets': { title: 'Support tickets', description: 'Omni-channel queue with SLA, tags, and internal notes.' },
  '/support/templates': { title: 'Support templates', description: 'Macros with variable interpolation and policy guardrails.' },
  '/cms/banners': { title: 'Banners', description: 'Geo-targeted hero assets with scheduling and preview.' },
  '/cms/promo-blocks': { title: 'Promo blocks', description: 'Composable lobby tiles with impression caps.' },
  '/cms/faq': { title: 'FAQ & help', description: 'Structured articles with localization and deep links.' },
  '/cms/notifications': { title: 'Notification templates', description: 'Push/email/in-app templates with compliance disclaimers.' },
  '/cms/announcements': { title: 'Announcement bar', description: 'Dismissible strip messaging with priority stacking.' },
  '/cms/popups': { title: 'Popups', description: 'Modal campaigns with frequency capping per segment.' },
  '/reports': { title: 'Reports hub', description: 'Saved views, scheduled exports, and governed access.' },
  '/settings/staff': { title: 'Admin users', description: 'Staff provisioning, MFA enforcement, and activity.' },
  '/settings/roles': { title: 'Roles & permissions', description: 'Fine-grained capability matrix with separation of duties.' },
  '/settings/platform': { title: 'Platform settings', description: 'Global knobs: currencies, rake caps, maintenance windows.' },
  '/settings/feature-flags': { title: 'Feature flags', description: 'Progressive rollout by segment and kill switches.' },
  '/settings/security': { title: 'Security settings', description: 'IP allowlists, webhook signing, and API keys.' },
}

/** Route-backed sidebar structure without icon concerns. */
export const navigationModel = [
  { title: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard' }] },
  {
    title: 'Players',
    items: [
      { to: '/players', label: 'Player list' },
      { to: '/players/new', label: 'Add player' },
    ],
  },
  {
    title: 'Wallet',
    items: [
      { to: '/wallet/deposits', label: 'Deposits', badgeKey: 'deposits' },
      { to: '/wallet/withdrawals', label: 'Withdrawals', badgeKey: 'withdrawals' },
      { to: '/wallet/adjustments', label: 'Adjustments' },
      { to: '/wallet/payment-methods', label: 'Payment methods' },
    ],
  },
  {
    title: 'Games',
    defaultOpen: true,
    items: [
      { to: '/tournaments', label: 'Tournaments' },
      { to: '/tables', label: 'Cash tables' },
      { to: '/sit-and-go', label: 'Sit & Go' },
    ],
  },
  {
    title: 'Fraud & security',
    items: [
      { to: '/fraud/alerts', label: 'Fraud alerts' },
      { to: '/fraud/reviews/shared-endpoints', label: 'Shared IP / device' },
      { to: '/fraud/reviews/collusion', label: 'Collusion review' },
      { to: '/fraud/reviews/chip-dumping', label: 'Chip dumping' },
      { to: '/fraud/sanctions', label: 'Sanctions' },
    ],
  },
  {
    title: 'KYC & compliance',
    items: [
      { to: '/kyc/queue', label: 'KYC queue', badgeKey: 'kyc' },
      { to: '/kyc/logs', label: 'Compliance logs' },
      { to: '/kyc/restrictions', label: 'Restrictions' },
    ],
  },
  {
    title: 'Promotions',
    items: [
      { to: '/promotions/bonuses', label: 'Bonuses' },
      { to: '/promotions/codes', label: 'Promo codes' },
      { to: '/promotions/rakeback', label: 'Rakeback / cashback' },
    ],
  },
  {
    title: 'VIP & engagement',
    items: [
      { to: '/vip/tiers', label: 'VIP tiers' },
      { to: '/vip/rules', label: 'Loyalty rules' },
      { to: '/leaderboards', label: 'Leaderboards' },
      { to: '/missions', label: 'Missions' },
      { to: '/achievements', label: 'Achievements' },
    ],
  },
  {
    title: 'Support',
    items: [
      { to: '/support/tickets', label: 'Tickets' },
      { to: '/support/templates', label: 'Templates' },
    ],
  },
  {
    title: 'CMS',
    items: [
      { to: '/cms/banners', label: 'Banners' },
      { to: '/cms/promo-blocks', label: 'Promo blocks' },
      { to: '/cms/faq', label: 'FAQ' },
      { to: '/cms/notifications', label: 'Notification templates' },
      { to: '/cms/announcements', label: 'Announcements' },
      { to: '/cms/popups', label: 'Popups' },
    ],
  },
  { title: 'Reports', items: [{ to: '/reports', label: 'Reports hub' }] },
  {
    title: 'Settings',
    items: [
      { to: '/settings/staff', label: 'Staff users' },
      { to: '/settings/roles', label: 'Roles & permissions' },
      { to: '/settings/platform', label: 'Platform' },
      { to: '/settings/feature-flags', label: 'Feature flags' },
      { to: '/settings/security', label: 'Security' },
    ],
  },
]
